/**
 * Lindy Collection Section JavaScript
 * Handles view toggle, sorting, items per page, pagination, and add to quote functionality
 */

/**
 * Trigger BSS B2B to re-process prices after AJAX content updates.
 * BSS uses MutationObserver and custom events to detect new elements.
 */
function triggerBssPriceRefresh() {
  // Method 1: Dispatch BSS's custom event to reinitialize price detection
  // This is BSS B2B's official way to trigger recalculation
  document.dispatchEvent(new CustomEvent('bss_b2b:CustomInitCartSubject'));

  // Method 2: If BSS B2B observer is available, notify it directly
  if (typeof BSS_B2B !== 'undefined' && BSS_B2B) {
    // Notify product observer about changes
    if (BSS_B2B.observer?.productSubject?.notifyObserver) {
      try {
        BSS_B2B.observer.productSubject.notifyObserver('CartPriceMutate', 'CollectionUpdate', {});
      } catch (e) {
        // Silently fail if method doesn't accept these params
      }
    }

    // Try productObserver as well
    if (BSS_B2B.observer?.productObserver?.notifyObserver) {
      try {
        BSS_B2B.observer.productObserver.notifyObserver('CartPriceMutate', 'CollectionUpdate', {});
      } catch (e) {
        // Silently fail
      }
    }
  }

  // Method 3: Fallback - dispatch scroll event to trigger any viewport-based detection
  window.dispatchEvent(new Event('scroll'));
}

// Make triggerBssPriceRefresh available globally
window.triggerBssPriceRefresh = triggerBssPriceRefresh;

class LindyCollectionSection {
  constructor() {
    // Prevent multiple instances
    if (LindyCollectionSection.instance) {
      return LindyCollectionSection.instance;
    }
    LindyCollectionSection.instance = this;

    this.globalClickHandlerBound = this.handleGlobalClick.bind(this);
    this.scrollHandlerBound = this.handleScroll.bind(this);
    this.init();
  }

  init() {
    this.isUpdating = false;
    this.setupViewToggle();
    this.setupSortBy();
    this.setupItemsPerPage();
    this.setupAddToQuote();
    this.setupMobileFilters();
    this.setupGlobalClickHandler();
    this.verifyCacheMatch();
  }

  /**
   * Verify content matches URL on page load (handles browser cache issues)
   */
  verifyCacheMatch() {
    const url = new URL(window.location.href);
    const urlPerPage = url.searchParams.get('per_page');

    if (urlPerPage && !this.isUpdating) {
      const displayedPerPage = document.getElementById('items-per-page-display')?.textContent?.trim();
      const productCount = document.querySelectorAll('.lindy-product-card').length;
      const expectedCount = parseInt(urlPerPage);

      // Check both the dropdown text AND actual product count
      const displayMismatch = displayedPerPage && displayedPerPage !== urlPerPage;
      const countMismatch = expectedCount > 24 && productCount <= 24;

      if (displayMismatch || countMismatch) {
        // Content doesn't match URL - fetch fresh content via AJAX
        this.updateProductGrid(url.toString());
      }
    }
  }

  /**
   * Fetch section HTML via Shopify Section Rendering API
   */
  async fetchSectionHTML(url, sectionId) {
    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', sectionId);

    // Use 'variant' parameter - Shopify ignores cache when variant is present
    // https://community.shopify.com/c/technical-q-a/variables-in-url-being-cached/td-p/1361289
    sectionUrl.searchParams.set('variant', Date.now().toString());

    const response = await fetch(sectionUrl.toString(), {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate'
      }
    });

    if (!response.ok) throw new Error('Failed to fetch section');
    return response.text();
  }

  /**
   * Update product grid via AJAX
   */
  async updateProductGrid(newUrl) {
    // Prevent concurrent updates
    if (this.isUpdating) return;
    this.isUpdating = true;

    // Find section by data attribute
    const sectionDiv = document.querySelector('.lindy-collection-section[data-section-id]');
    const sectionId = sectionDiv?.dataset.sectionId;

    if (!sectionId) {
      // Fallback to page navigation if section not found
      this.isUpdating = false;
      window.location.href = newUrl;
      return;
    }
    const productGrid = document.querySelector('.lindy-product-grid');

    // Show loading state
    if (productGrid) {
      productGrid.style.opacity = '0.5';
      productGrid.style.pointerEvents = 'none';
    }

    try {
      const html = await this.fetchSectionHTML(newUrl, sectionId);

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the new product grid
      const newProductGrid = doc.querySelector('.lindy-product-grid');
      const newPaginations = doc.querySelectorAll('.lindy-pagination');
      const newProductCounts = doc.querySelectorAll('.lindy-product-count');
      const newItemsPerPageDropdowns = doc.querySelectorAll('.lindy-items-per-page-dropdown');

      // Replace content
      if (productGrid && newProductGrid) {
        productGrid.innerHTML = newProductGrid.innerHTML;
        productGrid.style.opacity = '';
        productGrid.style.pointerEvents = '';
      }

      // Update all paginations
      const paginations = document.querySelectorAll('.lindy-pagination');
      paginations.forEach((pagination, index) => {
        if (newPaginations[index]) {
          pagination.innerHTML = newPaginations[index].innerHTML;
        }
      });

      // Update all product counts
      const productCounts = document.querySelectorAll('.lindy-product-count');
      productCounts.forEach((productCount, index) => {
        if (newProductCounts[index]) {
          productCount.innerHTML = newProductCounts[index].innerHTML;
        }
      });

      // Update all items per page dropdowns
      const itemsPerPageDropdowns = document.querySelectorAll('.lindy-items-per-page-dropdown');
      itemsPerPageDropdowns.forEach((dropdown, index) => {
        if (newItemsPerPageDropdowns[index]) {
          dropdown.innerHTML = newItemsPerPageDropdowns[index].innerHTML;
        }
      });

      // Re-setup items per page handlers for all dropdowns
      this.setupItemsPerPage();

      // Update URL without reload
      window.history.pushState({}, '', newUrl);

      // Re-setup add to quote for new products
      this.setupAddToQuote();

      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Trigger BSS B2B price refresh after DOM is painted
      requestAnimationFrame(() => {
        triggerBssPriceRefresh();
      });

      this.isUpdating = false;

    } catch (error) {
      // Fallback to page navigation on error
      this.isUpdating = false;
      window.location.href = newUrl;
    }
  }

  /**
   * Cleanup method to remove event listeners
   */
  destroy() {
    document.removeEventListener('click', this.globalClickHandlerBound);
    window.removeEventListener('scroll', this.scrollHandlerBound);
  }

  /**
   * Setup global click handler to close dropdowns when clicking outside
   */
  setupGlobalClickHandler() {
    // Remove any existing listener before adding new one
    document.removeEventListener('click', this.globalClickHandlerBound);
    document.addEventListener('click', this.globalClickHandlerBound);
    
    window.removeEventListener('scroll', this.scrollHandlerBound);
    window.addEventListener('scroll', this.scrollHandlerBound);
  }

  /**
   * Handle clicks outside dropdowns to close them
   */
  handleGlobalClick(e) {
    // Don't close if clicking inside a dropdown or trigger
    if (!e.target.closest('.lindy-sort-dropdown, .lindy-items-per-page-dropdown, .lindy-mobile-sort-btn, .lindy-mobile-sort-dropdown')) {
      this.closeAllDropdowns();
    }
  }

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns() {
    document.querySelectorAll('.sort-trigger, .items-per-page-trigger').forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.nextElementSibling.classList.add('is-hidden');
    });
    
    const mobileSortDropdown = document.querySelector('.lindy-mobile-sort-dropdown');
    const sortBtn = document.querySelector('.lindy-mobile-sort-btn');
    if (mobileSortDropdown) {
      mobileSortDropdown.style.display = 'none';
    }
    if (sortBtn) {
      sortBtn.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Handle scroll events to close dropdowns
   */
  handleScroll() {
    this.closeAllDropdowns();
  }

  /**
   * Setup view toggle between grid and list
   */
  setupViewToggle() {
    const viewToggleBtns = document.querySelectorAll('.view-toggle-btn');
    const productGrid = document.querySelector('.lindy-product-grid');

    if (!productGrid) return;

    // Get saved view from session storage
    const savedView = sessionStorage.getItem('lindyCollectionView') || 'grid';
    this.setView(savedView);

    viewToggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        this.setView(view);
        sessionStorage.setItem('lindyCollectionView', view);
      });
    });
  }

  /**
   * Set the view type (grid or list)
   */
  setView(view) {
    const viewToggleBtns = document.querySelectorAll('.view-toggle-btn');
    const productGrid = document.querySelector('.lindy-product-grid');

    if (!productGrid) return;

    productGrid.setAttribute('data-view', view);

    viewToggleBtns.forEach(btn => {
      if (btn.dataset.view === view) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  /**
   * Setup sort by functionality
   */
  setupSortBy() {
    const triggers = document.querySelectorAll('.sort-trigger');

    triggers.forEach(trigger => {
      // Toggle dropdown on click
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const menu = trigger.nextElementSibling;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        // Close all other dropdowns
        document.querySelectorAll('.sort-trigger, .items-per-page-trigger').forEach(t => {
          if (t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            t.nextElementSibling.classList.add('is-hidden');
          }
        });

        // Toggle this dropdown
        trigger.setAttribute('aria-expanded', !isExpanded);
        menu.classList.toggle('is-hidden', isExpanded);
      });

      // Handle menu option clicks
      const menu = trigger.nextElementSibling;
      const options = menu.querySelectorAll('.sort-option');

      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          const value = option.dataset.value;
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
        });
      });
    });
  }

  /**
   * Setup items per page functionality
   */
  setupItemsPerPage() {
    const triggers = document.querySelectorAll('.items-per-page-trigger');

    triggers.forEach(trigger => {
      // Toggle dropdown on click
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const menu = trigger.nextElementSibling;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        // Close all other dropdowns
        document.querySelectorAll('.items-per-page-trigger, .sort-trigger').forEach(t => {
          if (t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            t.nextElementSibling.classList.add('is-hidden');
          }
        });

        // Toggle this dropdown
        trigger.setAttribute('aria-expanded', !isExpanded);
        menu.classList.toggle('is-hidden', isExpanded);
      });

      // Handle menu option clicks
      const menu = trigger.nextElementSibling;
      const options = menu.querySelectorAll('.items-per-page-option');

      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          const href = option.getAttribute('href');
          if (href) {
            const url = new URL(href, window.location.origin);
            // Use AJAX to update content without page reload
            this.updateProductGrid(url.toString());
          }
        });
      });
    });
  }

  /**
   * Setup Add to Quote functionality
   */
  setupAddToQuote() {
    const quoteButtons = document.querySelectorAll('.lindy-btn--add-to-quote');

    quoteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const productId = btn.dataset.productId;
        this.addToQuote(productId, btn);
      });
    });
  }

  /**
   * Add product to quote (stored in localStorage)
   */
  addToQuote(productId, button) {
    // Get existing quote items from localStorage
    let quoteItems = JSON.parse(localStorage.getItem('lindyQuoteItems') || '[]');

    // Check if product already in quote
    if (!quoteItems.includes(productId)) {
      quoteItems.push(productId);
      localStorage.setItem('lindyQuoteItems', JSON.stringify(quoteItems));

      // Visual feedback
      const originalText = button.textContent;
      button.textContent = 'Added to Quote';
      button.style.backgroundColor = '#228B21';
      button.style.color = 'white';
      button.style.borderColor = '#228B21';

      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
        button.style.borderColor = '';
      }, 2000);

      // Dispatch custom event for other components to listen
      document.dispatchEvent(new CustomEvent('lindy:quoteUpdated', {
        detail: { quoteItems }
      }));
    } else {
      // Already in quote
      const originalText = button.textContent;
      button.textContent = 'Already in Quote';

      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Close the filters drawer and hide overlay
   */
  closeFiltersDrawer(filtersDrawer, overlay) {
    if (typeof filtersDrawer.close === 'function') {
      filtersDrawer.close();
    } else {
      // Fallback for native dialog element
      const dialog = filtersDrawer.querySelector('dialog');
      if (dialog) {
        dialog.close();
      }
    }

    // Hide overlay
    if (overlay) {
      overlay.classList.remove('active');
    }
  }

  /**
   * Setup mobile filter inline toggle
   */
  setupMobileFilters() {
    // Mobile Refine button - open filters drawer
    const refineBtn = document.querySelector('.lindy-mobile-refine-btn');
    const filtersDrawer = document.getElementById('filters-drawer');
    const overlay = document.querySelector('.lindy-filter-overlay');

    if (refineBtn && filtersDrawer) {
      refineBtn.addEventListener('click', () => {
        if (typeof filtersDrawer.showDialog === 'function') {
          filtersDrawer.showDialog();
        }
      });
    }

    // Mobile filter close button - close filters drawer
    const filterCloseBtn = document.querySelector('.lindy-filter-close');
    if (filterCloseBtn && filtersDrawer) {
      filterCloseBtn.addEventListener('click', () => {
        this.closeFiltersDrawer(filtersDrawer, overlay);
      });
    }

    // Click overlay to close drawer
    if (overlay && filtersDrawer) {
      overlay.addEventListener('click', () => {
        this.closeFiltersDrawer(filtersDrawer, overlay);
      });
    }

    const sortBtn = document.querySelector('.lindy-mobile-sort-btn');

    // Mobile Sort button - toggle inline sort dropdown
    if (sortBtn) {
      // Create a mobile sort dropdown if it doesn't exist
      let mobileSortDropdown = document.querySelector('.lindy-mobile-sort-dropdown');

      if (!mobileSortDropdown) {
        // Find the desktop sort dropdown to copy options from
        const desktopSortOptions = document.querySelectorAll('.sort-option');

        if (desktopSortOptions.length > 0) {
          // Create mobile sort dropdown
          mobileSortDropdown = document.createElement('div');
          mobileSortDropdown.className = 'lindy-mobile-sort-dropdown';
          mobileSortDropdown.style.display = 'none';

          desktopSortOptions.forEach(option => {
            const mobileOption = document.createElement('button');
            mobileOption.className = 'lindy-mobile-sort-option';
            mobileOption.textContent = option.textContent.trim();
            mobileOption.dataset.value = option.dataset.value;

            mobileOption.addEventListener('click', () => {
              // Update button text immediately for better UX
              const currentSortMobile = document.getElementById('current-sort-mobile');
              if (currentSortMobile) {
                currentSortMobile.textContent = mobileOption.textContent.trim();
              }

              const url = new URL(window.location.href);
              url.searchParams.set('sort_by', option.dataset.value);
              url.searchParams.delete('page');
              window.location.href = url.toString();
            });

            mobileSortDropdown.appendChild(mobileOption);
          });

          // Insert after sort button
          sortBtn.parentNode.insertBefore(mobileSortDropdown, sortBtn.nextSibling);
        }
      }

      sortBtn.addEventListener('click', () => {
        if (mobileSortDropdown) {
          const isOpen = mobileSortDropdown.style.display !== 'none';
          mobileSortDropdown.style.display = isOpen ? 'none' : 'block';
          sortBtn.setAttribute('aria-expanded', !isOpen);
        }
      });
    }

  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LindyCollectionSection();
  });
} else {
  new LindyCollectionSection();
}

// Re-initialize on Shopify section re-render (theme editor)
document.addEventListener('shopify:section:load', () => {
  new LindyCollectionSection();
  requestAnimationFrame(triggerBssPriceRefresh);
});

// Trigger BSS price refresh after filter updates
// The 'filter:update' event is dispatched by the theme's facets system
document.addEventListener('filter:update', () => {
  // Wait for section renderer to complete DOM updates, then trigger on next paint
  requestAnimationFrame(() => {
    requestAnimationFrame(triggerBssPriceRefresh);
  });
});
